import { Navigate, Outlet, Link, useLocation } from "react-router-dom"
import { LayoutDashboard, Users, LogOut, ChevronRight, Tag, CreditCard, Lightbulb } from "lucide-react"
import { useAuthStore } from "@/stores/authStore"
import { cn } from "@/lib/utils"
import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"

const ADMIN_NAV = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Users, label: "Users", path: "/admin/users" },
  { icon: CreditCard, label: "Plans", path: "/admin/plans" },
  { icon: Tag, label: "Coupons", path: "/admin/coupons" },
  { icon: Lightbulb, label: "Features", path: "/admin/feature-requests" },
]

export default function AdminLayout() {
  const { user, logout } = useAuthStore()
  const location = useLocation()

  if (!user || user.role !== "superadmin") {
    return <Navigate to="/" replace />
  }

  const isActive = (path: string) => {
    if (path === "/admin") return location.pathname === "/admin"
    return location.pathname.startsWith(path)
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Admin Sidebar */}
      <aside className="w-56 flex flex-col border-r border-border bg-card shrink-0">
        {/* Header */}
        <div className="flex items-center gap-2 h-14 px-4 border-b border-border">
          <img src="/app-icon.png" className="h-7 w-7 object-contain shrink-0" alt={APP_NAME} />
          <div>
            <p className="text-sm font-bold leading-none">{APP_NAME.toUpperCase()}</p>
            <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide">Admin</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {ADMIN_NAV.map(({ icon: Icon, label, path }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isActive(path)
                  ? "bg-red-600 text-white"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span>{label}</span>
            </Link>
          ))}
        </nav>

        {/* User + exit */}
        <div className="p-3 border-t border-border space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:bg-accent transition-colors"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            <span>Back to App</span>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
